<template>
  <v-dialog v-model="internalValue" max-width="800px" persistent scrollable>
    <v-card>
      <v-card-title>
        <v-icon left color="primary">mdi-pencil</v-icon>
        {{ $t('onboarding.edit-enrollment') }}
      </v-card-title>

      <v-divider class="mb-4"></v-divider>

      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.email"
                :label="$t('email') + ' *'"
                :rules="[rules.required, rules.email]"
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.companyName"
                :label="$t('company-name') + ' *'"
                :rules="[rules.required]"
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <organization-select
                v-model="form.accountNumber"
                :label="$t('account-number')"
                item-value="accountNumber"
                outlined
                clearable
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.siret"
                :label="$t('siret')"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.vatNumber"
                :label="$t('vat-number')"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.phone"
                :label="$t('phone')"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-select
            v-model="form.locale"
            :items="localeOptions"
            :label="$t('communication-language')"
            outlined
            dense
            :hint="$t('onboarding.locale-hint')"
            persistent-hint
          ></v-select>

          <v-divider class="my-4"></v-divider>
          <h3 class="text-subtitle-1 mb-3">{{ $t('address') }}</h3>

          <v-text-field
            v-model="form.address.street"
            :label="$t('street')"
            outlined
            dense
          ></v-text-field>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.address.city"
                :label="$t('city')"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.address.postalCode"
                :label="$t('postal-code')"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-text-field
            v-model="form.address.country"
            :label="$t('country')"
            outlined
            dense
          ></v-text-field>

          <!-- Organization Membership (read-only, for customers with attachToOrganization) -->
          <template v-if="enrollment.attachToOrganization">
            <v-divider class="my-4"></v-divider>
            <h3 class="text-subtitle-1 mb-3">{{ $t('onboarding.membership') }}</h3>
            <v-alert type="info" dense outlined class="mb-3">
              <div class="text-caption">{{ $t('onboarding.membership') }} - {{ $t('readonly') }}</div>
            </v-alert>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  :value="enrollment.attachToOrganization"
                  :label="$t('onboarding.attach-to-organization-account')"
                  outlined
                  dense
                  disabled
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="enrollment.memberType">
                <v-text-field
                  :value="enrollment.memberType"
                  :label="$t('onboarding.member-type')"
                  outlined
                  dense
                  disabled
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="enrollment.memberGroups">
                <v-text-field
                  :value="enrollment.memberGroups"
                  :label="$t('onboarding.member-groups')"
                  outlined
                  dense
                  disabled
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
          </template>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text small @click="close" :disabled="loading">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          small
          :loading="loading"
          :disabled="!valid"
          @click="save"
        >
          <v-icon left small>mdi-content-save</v-icon>
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import OrganizationSelect from '@/components/blocks/Select/OrganizationSelect.vue'
import CommonMixin from '@/components/mixins/CommonMixin'

export default {
  name: 'EditEnrollmentDialog',

  mixins: [CommonMixin],

  components: {
    OrganizationSelect
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    enrollment: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      error: null,
      form: {
        email: '',
        companyName: '',
        accountNumber: '',
        siret: '',
        vatNumber: '',
        phone: '',
        locale: 'fr',
        address: {
          street: '',
          city: '',
          postalCode: '',
          country: 'FR'
        }
      },
      localeOptions: [
        { text: 'Français', value: 'fr' },
        { text: 'English', value: 'en' },
        { text: 'Nederlands', value: 'nl' }
      ],
      rules: {
        required: v => !!v || this.$t('rules.field-required'),
        email: v => !v || /.+@.+\..+/.test(v) || this.$t('rules.email-invalid')
      }
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },

  watch: {
    value(val) {
      if (val && this.enrollment) {
        this.loadEnrollmentData()
      } else if (!val) {
        this.resetForm()
      }
    }
  },

  methods: {
    loadEnrollmentData() {
      this.form = {
        email: this.enrollment.email || '',
        companyName: this.enrollment.companyName || '',
        accountNumber: this.enrollment.accountNumber || '',
        siret: this.enrollment.siret || '',
        vatNumber: this.enrollment.vatNumber || '',
        phone: this.enrollment.phone || '',
        locale: this.enrollment.locale || 'fr',
        address: {
          street: this.enrollment.address?.street || '',
          city: this.enrollment.address?.city || '',
          postalCode: this.enrollment.address?.postalCode || '',
          country: this.enrollment.address?.country || 'FR'
        }
      }
    },

    resetForm() {
      this.form = {
        email: '',
        companyName: '',
        accountNumber: '',
        siret: '',
        vatNumber: '',
        phone: '',
        locale: 'fr',
        address: {
          street: '',
          city: '',
          postalCode: '',
          country: 'FR'
        }
      }
      this.error = null
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    async save() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.error = null

      try {
        // Ne pas envoyer les champs vides pour éviter d'écraser les données existantes
        const payload = {}
        if (this.form.email !== this.enrollment.email) payload.email = this.form.email
        if (this.form.companyName !== this.enrollment.companyName) payload.companyName = this.form.companyName
        if (this.form.accountNumber !== this.enrollment.accountNumber) payload.accountNumber = this.form.accountNumber || null
        if (this.form.siret !== this.enrollment.siret) payload.siret = this.form.siret || null
        if (this.form.vatNumber !== this.enrollment.vatNumber) payload.vatNumber = this.form.vatNumber || null
        if (this.form.phone !== this.enrollment.phone) payload.phone = this.form.phone || null
        if (this.form.locale !== this.enrollment.locale) payload.locale = this.form.locale

        // Adresse uniquement si modifiée
        const addressChanged =
          this.form.address.street !== (this.enrollment.address?.street || '') ||
          this.form.address.city !== (this.enrollment.address?.city || '') ||
          this.form.address.postalCode !== (this.enrollment.address?.postalCode || '') ||
          this.form.address.country !== (this.enrollment.address?.country || 'FR')

        if (addressChanged) {
          payload.address = this.form.address
        }

        const result = await PaymentOnboardingRepository.updateEnrollment(this.enrollment.id, payload)

        this.notifySuccess('onboarding.enrollment-updated-successfully')
        this.$emit('updated', result)
        this.close()
      } catch (err) {
        const errorCode = err.response?.data?.error
        const errorContext = err.response?.data?.context

        if (errorCode) {
          this.error = this.$t(errorCode, errorContext || {})
        } else {
          this.error = this.$t(err.response?.data?.message || err.message)
        }

        this.notifyError(this.error)
      } finally {
        this.loading = false
      }
    },

    close() {
      this.internalValue = false
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
